import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Download } from '@mui/icons-material';
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from 'xlsx';
import { NotificationProvider, AuthLayout } from "@1823-partners/core";
import Calculator from './Calculator';

function App() {
    const [iframeOpen, setIframeOpen] = useState(false);
    const [dataOpen, setDataOpen] = useState(false);
    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [capitalChargesData, setCapitalChargesData] = useState([]);
    const [capitalChargesMetadata, setCapitalChargesMetadata] = useState({ product: '', region: '', tenor: '' });

    useEffect(() => {
        if (capitalChargesData && capitalChargesData.length > 0) {
            const cols = Object.keys(capitalChargesData[0]).map((key) => ({
                field: key,
                headerName: key.replace(/_/g, " "),
                flex: 1,
                valueGetter: (value) => {
                    if (["RATING", "SIZE_FACTOR", "TARGET", "ASOFDT"].includes(key.toUpperCase())) {
                        return value;
                    }
                    return `${(value * 100).toFixed(2)}%`;
                }
            }));
            const rowData = capitalChargesData.map((row, index) => ({ id: index, ...row }));
            setColumns(cols);
            setRows(rowData);
            setDataLoaded(true);
        }
    }, [capitalChargesData]);

    const handleCapitalChargesUpdate = (data, metadata) => {
        setCapitalChargesData(data);
        setCapitalChargesMetadata(metadata);
    };

    const handleDownload = () => {
        const filtered = rows.map(({ id, ...rest }) => rest);
        const worksheet = XLSX.utils.json_to_sheet(filtered);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
        XLSX.writeFile(workbook, "rating_vectors.xlsx");
    };

    const extraButtons = (
        <>
            <Button onClick={() => setDataOpen(true)} sx={{ backgroundColor: 'lightgrey', color: 'black', m: 1 }}>
                Capital Charges
            </Button>
            <Button onClick={() => setIframeOpen(true)} sx={{ backgroundColor: 'lightgrey', color: 'black', m: 1 }}>
                YTD Sales PBI
            </Button>
        </>
    );

    return (
        <NotificationProvider>
            <AuthLayout
                title="ROE Calculator"
                headerContent={extraButtons}
                contentSx={{ p: 0 }}
            >
                {() => (
                    <>
                        <Calculator onCapitalChargesUpdate={handleCapitalChargesUpdate} />
                        <Dialog open={iframeOpen} onClose={() => setIframeOpen(false)} maxWidth="lg" fullWidth>
                            <DialogTitle>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <span>YTD Realized Sales</span>
                                    <Button variant="contained" onClick={() => setIframeOpen(false)} startIcon={<CloseIcon />} sx={{ backgroundColor: "lightgrey", color: "black" }}>
                                        Close
                                    </Button>
                                </Box>
                            </DialogTitle>
                            <DialogContent sx={{ position: 'relative', p: 0 }}>
                                <iframe
                                    title="YTD Realized Sales"
                                    width="100%"
                                    height="800px"
                                    style={{ border: 'none' }}
                                    src="https://app.powerbi.com/reportEmbed?reportId=38283418-2c09-4ce5-9553-3f065b06eafe&appId=f045aaac-07f4-4d17-94b2-a8bcfb3bae05&autoAuth=true&ctid=f6deacae-21da-4d08-8b95-5126e1311db2"
                                />
                            </DialogContent>
                        </Dialog>
                        <Dialog open={dataOpen} onClose={() => setDataOpen(false)} maxWidth="lg" fullWidth>
                            <DialogTitle>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <span>
                                        Capital Charges
                                        {capitalChargesMetadata.product && (
                                            <span style={{ fontWeight: 'normal', fontSize: '0.9em', marginLeft: '10px' }}>
                                                {capitalChargesMetadata.product} | {capitalChargesMetadata.region} | {capitalChargesMetadata.tenor}
                                            </span>
                                        )}
                                    </span>
                                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                        <Button variant="contained" onClick={handleDownload} startIcon={<Download />} sx={{ backgroundColor: "lightgrey", color: "black" }}>
                                            Download
                                        </Button>
                                        <Button variant="contained" onClick={() => setDataOpen(false)} startIcon={<CloseIcon />} sx={{ backgroundColor: "lightgrey", color: "black" }}>
                                            Close
                                        </Button>
                                    </Box>
                                </Box>
                            </DialogTitle>
                            <DialogContent sx={{ position: 'relative', p: 0 }}>
                                {dataLoaded ? (
                                    <Box sx={{ m: 2 }}>
                                        <DataGrid rows={rows} columns={columns} />
                                    </Box>
                                ) : (<Typography>Loading data...</Typography>)}
                            </DialogContent>
                        </Dialog>
                    </>
                )}
            </AuthLayout>
        </NotificationProvider>
    );
}

export default App;
